// MaintenanceDashboard.js - Complete with Rejected Completion Handling

import React, { useState, useEffect, useRef } from 'react';
import Axios from 'axios';
import { Host } from '../../App';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import 'bootstrap/dist/css/bootstrap.min.css';
import './MaintenanceDashboard.css';

import TicketDetail from './TicketDetail';
import AllRequests from './All_requests';
import ServiceTypeTable from './ServiceTypeTable';
import MaintenanceForm from './Maintenance_request';
import NewShiftReport from './Daily_maintenance_Reports';
import ShiftReportAll from './All_Maintenance_Reports';

// Timezone-safe date parser
const parseLocalDate = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput;
  const str = String(dateInput);
  if (str.includes('T') || /\d{2}:\d{2}:\d{2}/.test(str)) {
    return new Date(str);
  }
  return new Date(str + 'T00:00:00');
};

const MaintenanceDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    stats: {
      total: 0,
      open: 0,
      inProgress: 0,
      completed: 0,
      closed: 0,
      overdue: 0,
      highPriority: 0,
      critical: 0,
      pendingApproval: 0,
      rejected: 0
    },
    recentTickets: [],
    upcomingTasks: [],
    calendarEvents: [],
    teamStatus: [],
    performanceMetrics: {
      avgCompletionTime: 0,
      onTimeRate: 0,
      totalHours: 0
    },
    pendingNewRequests: [],
    pendingModificationRequests: [],
    pendingCompletionRequests: [],
    overdueTickets: []
  });

  const [showOnlyApproved, setShowOnlyApproved] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().slice(0, 10),
    end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [calendarRef, setCalendarRef] = useState(null);
  const [activeQuickMenu, setActiveQuickMenu] = useState(false);
  const [allTickets, setAllTickets] = useState([]);

  const [Area, setArea] = useState('DASHBOARD');
  const [activeTicketId, setActiveTicketId] = useState(null);

  const [showPopup, setShowPopup] = useState(false);
  const [popupTickets, setPopupTickets] = useState([]);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [popupTitle, setPopupTitle] = useState('Overdue Tickets');
  const [popupType, setPopupType] = useState('overdue');

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approve');
  const [selectedApprovalTicket, setSelectedApprovalTicket] = useState(null);
  const [approvalComments, setApprovalComments] = useState('');
  const [approvalDate, setApprovalDate] = useState('');
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const [editedTicketData, setEditedTicketData] = useState({}); // NEW: holds manager edits before approval

  // Collapsible tables state
  const [showNewRequests, setShowNewRequests] = useState(true);
  const [showModificationRequests, setShowModificationRequests] = useState(true);
  const [showCompletionRequests, setShowCompletionRequests] = useState(true);

  const token = window.localStorage.getItem('acessToken');
  const userName = JSON.parse(window.localStorage.getItem('Name'));

  // ===== CHECK IF TICKET HAS REJECTED COMPLETION =====
  const isCompletionRejected = (ticket) => {
    return ticket.approval_status === 'Rejected' && ticket.approval_type === 'completion';
  };

  // ===== GET STATUS LABEL FOR DISPLAY =====
  const getStatusLabel = (ticket) => {
    if (isCompletionRejected(ticket)) {
      return 'Completion Rejected';
    }
    const labels = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'review': 'Review',
      'completed': 'Completed',
      'closed': 'Closed',
      'cancelled': 'Cancelled'
    };
    return labels[ticket.status] || ticket.status;
  };

  // ===== GET STATUS COLOR FOR DISPLAY =====
  const getStatusColorForDisplay = (ticket) => {
    if (isCompletionRejected(ticket)) {
      return '#dc3545';
    }
    return getTicketStatusColor(ticket.status);
  };

  useEffect(() => {
    checkUserRole();
    fetchAllData();
    const interval = setInterval(() => {
      checkOverdueTickets();
    }, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if ((dashboardData.calendarEvents && dashboardData.calendarEvents.length > 0) ||
      (dashboardData.recentTickets && dashboardData.recentTickets.length > 0)) {
      checkOverdueTickets();
    }
  }, [dashboardData.calendarEvents, dashboardData.recentTickets]);

  const checkUserRole = async () => {
    try {
      const response = await Axios.get(`https://` + Host + `/api/user-info`, {
        params: { token }
      });
      if (response.data) {
        setIsManager(response.data.Stage === '2' || response.data.Ranked === 'Admin' || response.data.Section === 'Manager');
      }
    } catch (error) {
      console.error('Error checking user role:', error);
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const response = await Axios.get(`https://` + Host + `/maintenance/api/dashboard/data`, {
        params: { token }
      });
      console.log('Dashboard data fetched:', response.data);

      if (response.data && response.data.success) {
        let allTicketsFromAPI = [];

        if (response.data.recentTickets) {
          allTicketsFromAPI = [...allTicketsFromAPI, ...response.data.recentTickets];
        }
        if (response.data.calendarEvents) {
          allTicketsFromAPI = [...allTicketsFromAPI, ...response.data.calendarEvents];
        }
        if (response.data.pendingNewRequests) {
          allTicketsFromAPI = [...allTicketsFromAPI, ...response.data.pendingNewRequests];
        }
        if (response.data.pendingModificationRequests) {
          allTicketsFromAPI = [...allTicketsFromAPI, ...response.data.pendingModificationRequests];
        }
        if (response.data.pendingCompletionRequests) {
          allTicketsFromAPI = [...allTicketsFromAPI, ...response.data.pendingCompletionRequests];
        }

        const uniqueTickets = allTicketsFromAPI.filter((ticket, index, self) =>
          index === self.findIndex(t => (t.id || t.ticket_id) === (ticket.id || ticket.ticket_id))
        );

        setAllTickets(uniqueTickets);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const stats = {
          total: uniqueTickets.length,
          open: uniqueTickets.filter(t => t.status === 'open').length,
          inProgress: uniqueTickets.filter(t => t.status === 'in_progress' || t.status === 'inprogress').length,
          completed: uniqueTickets.filter(t => t.status === 'completed').length,
          closed: uniqueTickets.filter(t => t.status === 'closed').length,
          highPriority: uniqueTickets.filter(t => t.priority === 'High' || t.priority === 'high').length,
          critical: uniqueTickets.filter(t => t.priority === 'Critical' || t.priority === 'critical').length,
          pendingApproval: uniqueTickets.filter(t => t.approval_status === 'Pending' || t.status === 'pending_approval').length,
          rejected: uniqueTickets.filter(t => t.approval_status === 'Rejected' && t.approval_type === 'completion').length,
          overdue: 0
        };

        const uniqueOverdue = response.data.overdueTickets || [];
        stats.overdue = uniqueOverdue.length;

        console.log('📊 Stats calculated:', stats);

        setDashboardData(prev => ({
          ...prev,
          stats: stats,
          recentTickets: response.data.recentTickets || [],
          calendarEvents: response.data.calendarEvents || [],
          teamStatus: response.data.teamStatus || [],
          performanceMetrics: response.data.performanceMetrics || {
            avgCompletionTime: 0,
            onTimeRate: 0,
            totalHours: 0
          },
          pendingNewRequests: response.data.pendingNewRequests || [],
          pendingModificationRequests: response.data.pendingModificationRequests || [],
          pendingCompletionRequests: response.data.pendingCompletionRequests || [],
          overdueTickets: response.data.overdueTickets || []
        }));

        setPopupDismissed(false);
      } else {
        console.error('API returned error:', response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkOverdueTickets = () => {
    const uniqueOverdue = dashboardData.overdueTickets || [];

    console.log('📊 Overdue tickets check:', {
      total: uniqueOverdue.length,
      details: uniqueOverdue.map(t => ({
        id: t.ticket_id || t.id,
        ticket_number: t.ticket_number,
        date: t.event_date || t.tentative_completion_date,
        status: t.status
      }))
    });

    if (uniqueOverdue.length > 0 && !popupDismissed) {
      setPopupTickets(uniqueOverdue);
      setPopupTitle('Overdue Tickets');
      setPopupType('overdue');
      setShowPopup(true);
    } else if (uniqueOverdue.length === 0) {
      setShowPopup(false);
    }
  };

  const getTicketsByType = (type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const calendarEvents = dashboardData.calendarEvents || [];
    const recentTickets = dashboardData.recentTickets || [];

    switch (type) {
      case 'total':
        return allTickets;
      case 'open':
        return allTickets.filter(t => t.status === 'open');
      case 'inProgress':
        return allTickets.filter(t => t.status === 'in_progress' || t.status === 'inprogress');
      case 'completed':
        return allTickets.filter(t => t.status === 'completed');
      case 'closed':
        return allTickets.filter(t => t.status === 'closed');
      case 'overdue':
        return dashboardData.overdueTickets || [];
      case 'highPriority':
        return allTickets.filter(t => t.priority === 'High' || t.priority === 'high');
      case 'critical':
        return allTickets.filter(t => t.priority === 'Critical' || t.priority === 'critical');
      case 'pendingApproval':
        return allTickets.filter(t => t.approval_status === 'Pending' || t.status === 'pending_approval');
      case 'rejected':
        return allTickets.filter(t => t.approval_status === 'Rejected' && t.approval_type === 'completion');
      default:
        return [];
    }
  };

  const showFilteredPopup = (type, title) => {
    const tickets = getTicketsByType(type);
    if (!tickets || tickets.length === 0) {
      alert(`No ${title.toLowerCase()} found`);
      return;
    }
    setPopupTickets(tickets);
    setPopupTitle(title);
    setPopupType(type);
    setPopupDismissed(false);
    setShowPopup(true);
  };

  const getTicketStatusColor = (status) => {
    const statusColors = {
      'open': '#f59e0b',
      'in_progress': '#3b82f6',
      'review': '#8b5cf6',
      'completed': '#22c55e',
      'closed': '#64748b',
      'cancelled': '#ef4444',
      'overdue': '#dc2626',
      'pending_approval': '#8b5cf6'
    };
    return statusColors[status] || '#94a3b8';
  };

  const getTicketStatusIcon = (status) => {
    const statusIcons = {
      'open': '📋',
      'in_progress': '🔄',
      'review': '👁️',
      'completed': '✅',
      'closed': '🔒',
      'cancelled': '❌',
      'overdue': '⚠️',
      'pending_approval': '⏳'
    };
    return statusIcons[status] || '📄';
  };

  const getEventColor = (event) => {
    // Rejected completions always take priority, regardless of status or date.
    if (event.approval_status === 'Rejected' && event.approval_type === 'completion') {
      return '#dc3545'; // Rejected
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let eventDate = null;
    if (event.event_date) {
      eventDate = parseLocalDate(event.event_date);
      if (eventDate) eventDate.setHours(0, 0, 0, 0);
    }

    // Finished tickets get their status color no matter what the date says.
    if (event.status === 'completed' || event.status === 'closed' || event.status === 'cancelled') {
      return getTicketStatusColor(event.status);
    }

    // Still-open tickets whose date has passed are overdue.
    if (eventDate && !isNaN(eventDate.getTime()) && eventDate < today) {
      return '#dc2626'; // Overdue
    }

    // Submitted / awaiting approval.
    if (event.approval_status === 'Pending' || event.status === 'pending_approval') {
      return '#8b5cf6'; // Pending / Submitted
    }

    // Everything else (open, in_progress) — status color.
    return getTicketStatusColor(event.status);
  };

  const handleTicketClick = (ticketId) => {
    console.log('Opening ticket with ID:', ticketId);
    if (!ticketId) {
      console.error('No ticket ID provided');
      return;
    }
    setActiveTicketId(ticketId);
    setArea('TICKET_DETAIL');
    setShowPopup(false);
  };

  const handleDateSelect = (selectInfo) => { };

  const handleEventClick = (clickInfo) => {
    const eventId = clickInfo.event.id;
    console.log('Event clicked:', eventId);
    if (eventId && eventId.startsWith('ticket-')) {
      const ticketId = eventId.replace('ticket-', '');
      if (ticketId && ticketId !== 'undefined') {
        handleTicketClick(ticketId);
      }
    }
  };

  const renderStatCard = (icon, label, value, color, subtitle = '', onClick = null, filterType = null) => {
    const handleClick = () => {
      if (filterType) {
        showFilteredPopup(filterType, label);
      }
      if (onClick) onClick();
    };

    return (
      <div className={`stat-card-enhanced ${color} clickable-stat`} onClick={handleClick} style={{ cursor: 'pointer' }}>
        <div className="stat-card-glow"></div>
        <div className="stat-icon-enhanced">{icon}</div>
        <div className="stat-content-enhanced">
          <div className="stat-value-enhanced">{value}</div>
          <div className="stat-label-enhanced">{label}</div>
          {subtitle && <div className="stat-subtitle-enhanced">{subtitle}</div>}
        </div>
        <div className="stat-card-pattern"></div>
      </div>
    );
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setPopupDismissed(true);
  };

  const handlePopupTicketClick = (ticketId) => {
    console.log('Popup ticket clicked:', ticketId);
    if (ticketId) {
      setShowPopup(false);
      setPopupDismissed(true);
      setActiveTicketId(ticketId);
      setArea('TICKET_DETAIL');
    }
  };

  // ===== OPEN APPROVAL MODAL =====
  const openApprovalModal = (ticket, action) => {
    setSelectedApprovalTicket(ticket);
    setApprovalAction(action);
    setApprovalComments('');
    setApprovalError('');

    const defaultDate = new Date().toISOString().slice(0, 10);
    setApprovalDate(defaultDate);

    // NEW: preload editable fields only for new-request approvals
    if (action === 'approve' && (!ticket.approval_type || ticket.approval_type === 'new')) {
      setEditedTicketData({
        issue_description: ticket.issue_description || '',
        maintenance_category: ticket.maintenance_category || '',
        type: ticket.type || '',
        priority: ticket.priority || 'Low',
        assigned_completion_date: ticket.assigned_completion_date ? ticket.assigned_completion_date.slice(0, 10) : '',
        tentative_completion_date: ticket.tentative_completion_date ? ticket.tentative_completion_date.slice(0, 10) : '',
        schedule_start_date: ticket.schedule_start_date ? ticket.schedule_start_date.slice(0, 10) : '',
        schedule_end_date: ticket.schedule_end_date ? ticket.schedule_end_date.slice(0, 10) : ''
      });
    } else {
      setEditedTicketData({});
    }

    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async () => {
    if (!selectedApprovalTicket) return;

    setIsSubmittingApproval(true);
    try {
      const todayDate = new Date().toISOString().slice(0, 10);

      // NEW: If manager edited fields on a new-request approval, PATCH those changes first
      // using the same edit endpoint already used elsewhere in the app.
      const isNewRequestApproval = approvalAction === 'approve' &&
        (!selectedApprovalTicket.approval_type || selectedApprovalTicket.approval_type === 'new');

      if (isNewRequestApproval) {
        const fields = [
          'issue_description',
          'maintenance_category',
          'type',
          'priority',
          'assigned_completion_date',
          'tentative_completion_date',
          'schedule_start_date',
          'schedule_end_date'
        ];

        const changes = {};
        let hasChanges = false;
        fields.forEach(field => {
          const original = selectedApprovalTicket[field]
            ? String(selectedApprovalTicket[field]).slice(0, 10)
            : '';
          const edited = editedTicketData[field] || '';
          if (edited !== original && edited !== (selectedApprovalTicket[field] || '')) {
            changes[field] = editedTicketData[field];
            hasChanges = true;
          }
        });

        if (hasChanges) {
          changes.manager_comments = `Changes applied by manager before approval: ${approvalComments || 'No reason provided'}`;

          await Axios.patch(
            `https://` + Host + `/maintenance/api/tickets/${selectedApprovalTicket.id}`,
            changes,
            { params: { token } }
          );
        }
      }

      const payload = {
        token,
        ticketId: selectedApprovalTicket.id || selectedApprovalTicket.ticket_id,
        action: approvalAction,
        approved_by: userName,
        manager_comments: approvalComments,
        approval_date: todayDate,
        approval_type: selectedApprovalTicket.approval_type || 'new'
      };

      await Axios.post(`https://` + Host + `/maintenance/api/tickets/approve`, payload);

      alert(`Request ${approvalAction === 'approve' ? 'approved' : 'rejected'} successfully!`);
      setShowApprovalModal(false);
      fetchAllData();
    } catch (error) {
      console.error('Error processing approval:', error);
      alert(error.response?.data?.error || 'Failed to process approval');
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  // ===== HELPER: Check if ticket is an instance =====
  const isInstance = (ticket) => {
    if (ticket.ticket_number && ticket.ticket_number.startsWith('INST-')) {
      return true;
    }
    if (ticket.recurrence_parent_id && ticket.recurrence_parent_id !== 0 && ticket.recurrence_parent_id !== null) {
      return true;
    }
    if (ticket.parent_ticket_id && ticket.parent_ticket_id !== 0 && ticket.parent_ticket_id !== null) {
      return true;
    }
    if (ticket.instance_number && ticket.instance_number > 0) {
      return true;
    }
    return false;
  };

  // ===== RENDER APPROVAL TABLE WITH COLLAPSE =====
  const renderApprovalTable = (title, tickets, typeLabel, emptyMessage, isOpen, toggleOpen) => {
    const getIcon = () => {
      switch (typeLabel) {
        case 'new': return 'fa-file-plus';
        case 'modification': return 'fa-pen-to-square';
        case 'completion': return 'fa-check-double';
        default: return 'fa-tasks';
      }
    };

    const getColor = () => {
      switch (typeLabel) {
        case 'new': return '#3b82f6';
        case 'modification': return '#f59e0b';
        case 'completion': return '#22c55e';
        default: return '#8b5cf6';
      }
    };

    let filteredTickets = tickets;
    if (typeLabel === 'new') {
      filteredTickets = tickets?.filter(ticket => !isInstance(ticket)) || [];
    }

    return (
      <div className="approval-table-wrapper">
        <div className="approval-table-header" onClick={toggleOpen} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="approval-icon-wrapper" style={{ background: getColor() }}>
              <i className={`fas ${getIcon()}`}></i>
            </div>
            <div>
              <h4 className="approval-table-title">{title}</h4>
              <span className="approval-table-subtitle">
                {isOpen ? 'Click to collapse' : 'Click to expand'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className={`approval-count-badge ${typeLabel === 'new' ? 'badge-primary' : typeLabel === 'modification' ? 'badge-warning' : 'badge-success'}`}>
              {filteredTickets?.length || 0}
            </span>
            <i className={`fas ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} approval-toggle-icon`}></i>
          </div>
        </div>
        {isOpen && (
          <>
            {(!filteredTickets || filteredTickets.length === 0) ? (
              <div className="approval-table-empty">
                <i className="fas fa-check-circle"></i>
                <p>{emptyMessage || `No ${title.toLowerCase()} pending`}</p>
                <span className="approval-empty-sub">All caught up! 🎉</span>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover approval-table">
                  <thead>
                    <tr>
                      <th>Ticket #</th>
                      <th>Issue</th>
                      <th>Requester</th>
                      <th>Type</th>
                      <th>Priority</th>
                      <th>Details</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map(ticket => {
                      const isRejected = isCompletionRejected(ticket);
                      return (
                        <tr key={ticket.id} className={`approval-row ${isRejected ? 'rejected-row' : ''}`}>
                          <td>
                            <a href="#" onClick={() => handleTicketClick(ticket.id)} className="ticket-link">
                              {ticket.ticket_number}
                            </a>
                            {ticket.is_recurring && (
                              <span className="recurring-badge" title="Recurring">
                                <i className="fas fa-sync-alt"></i>
                              </span>
                            )}
                            {isRejected && (
                              <span className="rejection-badge">
                                <i className="fas fa-times-circle"></i> Rejected
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="issue-cell">
                              <div className="issue-description">{ticket.issue_description}</div>
                              {ticket.maintenance_category && (
                                <div className="issue-category">{ticket.maintenance_category}</div>
                              )}
                              {isRejected && ticket.manager_comments && (
                                <div className="rejection-feedback" style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>
                                  <i className="fas fa-comment"></i> {ticket.manager_comments}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="requester-cell">
                              <span className="requester-avatar">{ticket.requested_by?.charAt(0) || 'U'}</span>
                              <span className="requester-name">{ticket.requested_by || 'Unassigned'}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`type-badge ${ticket.type === 'prescheduled' ? 'prescheduled' : 'emergent'}`}>
                              {ticket.type === 'prescheduled' ? '📅 Scheduled' : '⚡ Emergent'}
                            </span>
                          </td>
                          <td>
                            <span className={`priority-badge ${ticket.priority?.toLowerCase() || 'low'}`}>
                              {ticket.priority || 'Low'}
                            </span>
                          </td>
                          <td>
                            {typeLabel === 'modification' && ticket.pending_data && (
                              <button
                                className="btn btn-sm btn-outline-info view-changes-btn"
                                onClick={() => {
                                  try {
                                    const changes = JSON.parse(ticket.pending_data);
                                    alert('Proposed Changes:\n' + JSON.stringify(changes, null, 2));
                                  } catch (e) {
                                    alert(ticket.pending_data);
                                  }
                                }}
                              >
                                <i className="fas fa-eye"></i> View Changes
                              </button>
                            )}
                            {typeLabel === 'completion' && ticket.pending_data && (
                              <div className="completion-details">
                                {ticket.pending_data.completion_notes && (
                                  <div className="completion-notes">
                                    <i className="fas fa-comment"></i> {ticket.pending_data.completion_notes}
                                  </div>
                                )}
                              </div>
                            )}
                            {typeLabel === 'new' && (
                              <div className="schedule-detail">
                                <i className="fas fa-calendar-alt"></i>
                                {ticket.type === 'prescheduled' || ticket.is_recurring ? (
                                  <span>Effective: {ticket.schedule_start_date ? ticket.schedule_start_date.slice(0, 10) : 'Not set'}</span>
                                ) : (
                                  <span>Tentative: {ticket.tentative_completion_date ? ticket.tentative_completion_date.slice(0, 10) : 'Not set'}</span>
                                )}
                                {ticket.is_recurring && ticket.recurrence_type && ticket.recurrence_type !== 'none' && (
                                  <div className="recurrence-info">
                                    <i className="fas fa-sync-alt"></i> {ticket.recurrence_type}
                                    {ticket.recurrence_day && ` (${ticket.recurrence_day})`}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            {isManager && (
                              <div className="approval-actions-modern">
                                {!isRejected && (
                                  <>
                                    <button
                                      className="action-btn approve-btn-modern"
                                      onClick={() => openApprovalModal(ticket, 'approve')}
                                      title="Approve"
                                    >
                                      <i className="fas fa-check"></i>
                                    </button>
                                    <button
                                      className="action-btn reject-btn-modern"
                                      onClick={() => openApprovalModal(ticket, 'reject')}
                                      title="Reject"
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  </>
                                )}
                                {isRejected && (
                                  <button
                                    className="action-btn reopen-btn-modern"
                                    onClick={() => handleTicketClick(ticket.id)}
                                    title="Reopen Ticket"
                                    style={{ background: '#ffc107', color: '#000' }}
                                  >
                                    <i className="fas fa-undo"></i>
                                  </button>
                                )}
                                <button
                                  className="action-btn view-btn-modern"
                                  onClick={() => handleTicketClick(ticket.id)}
                                  title="View Details"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ===== GET FILTERED EVENTS - SHOW ONLY INSTANCES, HIDE PARENTS =====
  const getFilteredEvents = () => {
    const backendEvents = dashboardData.calendarEvents || [];
    const sourceTickets = allTickets || [];

    // Tickets that already have at least one event from the backend
    // (includes projected future recurring instances).
    const eventTicketIds = new Set(backendEvents.map(e => e.ticket_id));

    // Any ticket with no backend event gets a synthetic one so it still
    // shows up on the calendar. Falls back through the most meaningful
    // date available, and finally to created_at so nothing is dropped.
    const syntheticEvents = sourceTickets
      .filter(t => !eventTicketIds.has(t.id))
      .map(t => {
        const fallbackDate =
          t.tentative_completion_date ||
          t.schedule_start_date ||
          t.request_date ||
          t.created_at;
        if (!fallbackDate) return null;
        return {
          ticket_id: t.id,
          ticket_number: t.ticket_number,
          title: t.issue_description,
          event_date: String(fallbackDate).slice(0, 10),
          status: t.status,
          priority: t.priority,
          approval_status: t.approval_status,
          approval_type: t.approval_type,
          manager_comments: t.manager_comments,
          event_type: t.type === 'emergent' ? 'Emergency' : (t.maintenance_category || 'General'),
          is_recurring: !!t.is_recurring,
          instance_number: 0,
          maintenance_category: t.maintenance_category,
          requested_by: t.requested_by,
          assigned_to: t.assigned_to,
          recurrence_parent_id: t.recurrence_parent_id
        };
      })
      .filter(Boolean);

    const events = [...backendEvents, ...syntheticEvents];

    console.log('📅 Total calendar events before filtering:', events.length);

    if (events.length === 0) {
      console.log('No calendar events available');
      return [];
    }

    // Step 1: Find ALL parent IDs
    const parentIds = new Set();

    events.forEach(event => {
      if (event.recurrence_parent_id && event.recurrence_parent_id !== 0 && event.recurrence_parent_id !== null) {
        parentIds.add(event.recurrence_parent_id);
      }
    });

    console.log('📅 Parent IDs to HIDE:', Array.from(parentIds));

    // Step 2: Filter events
    // Step 2: Filter events — show everything (open, in progress, completed,
    // closed, cancelled, pending/submitted, rejected), only hide recurring
    // parent tickets so instances take their place.
    let filtered = events.filter(event => {
      const isInstanceEvent =
        (event.recurrence_parent_id !== undefined &&
          event.recurrence_parent_id !== null &&
          event.recurrence_parent_id !== 0 &&
          event.recurrence_parent_id !== '0') ||
        (event.ticket_number && event.ticket_number.startsWith('INST-'));

      const isRecurringEvent =
        event.is_recurring === true || event.is_recurring === 1 || event.is_recurring === '1';

      // HIDE parents
      if (isRecurringEvent && !isInstanceEvent) {
        return false;
      }

      return true;
    });

    // Map to FullCalendar format
    const mappedEvents = filtered
      .filter(event => {
        const hasId = event.ticket_id !== undefined && event.ticket_id !== null;
        const hasDate = event.event_date !== undefined && event.event_date !== null;
        if (!hasId || !hasDate) {
          console.warn('Skipping event missing ticket_id or event_date:', event);
          return false;
        }
        return true;
      })
      .map(event => {
        const color = getEventColor(event);

        const eventDateCheck = parseLocalDate(event.event_date);
        if (!eventDateCheck || isNaN(eventDateCheck.getTime())) {
          console.warn('Invalid event date:', event.event_date);
          return null;
        }

        let titlePrefix = '';
        if (event.event_type === 'Emergency') {
          titlePrefix = '🚨 ';
        } else if (event.event_type === 'Pre-Scheduled') {
          titlePrefix = '📅 ';
        } else if (event.event_type === 'Facility') {
          titlePrefix = '🏗️ ';
        }

        let title = event.title || event.issue_description || `Ticket ${event.ticket_number || event.ticket_id}`;
        if (title && title.length > 30) {
          title = title.substring(0, 27) + '...';
        }
        title = `${titlePrefix}${title || 'No Title'}`;

        const eventId = event.is_recurring && event.instance_number
          ? `ticket-${event.ticket_id}-${event.instance_number}`
          : `ticket-${event.ticket_id}`;

        return {
          id: eventId,
          title: title,
          start: event.event_date,
          allDay: true,
          backgroundColor: color,
          borderColor: color,
          textColor: '#ffffff',
          extendedProps: {
            ticketId: event.ticket_id,
            status: event.status,
            approval_status: event.approval_status,
            is_recurring: event.is_recurring || false,
            event_type: event.event_type,
            instance_number: event.instance_number || 0,
            ticket_number: event.ticket_number
          }
        };
      })
      .filter(event => event !== null);

    return mappedEvents;
  };

  if (isLoading) {
    return (
      <div className="dashboard-loader-enhanced">
        <div className="loader-spinner-enhanced">
          <div className="spinner-circle"></div>
          <div className="loader-text">Loading Dashboard...</div>
          <div className="loader-progress-bar">
            <div className="loader-progress-fill"></div>
          </div>
        </div>
      </div>
    );
  }

  const { stats, recentTickets, calendarEvents, teamStatus, performanceMetrics, pendingNewRequests, pendingModificationRequests, pendingCompletionRequests } = dashboardData;

  const totalPending =
    (pendingNewRequests?.filter(t => !isInstance(t))?.length || 0) +
    (pendingModificationRequests?.length || 0) +
    (pendingCompletionRequests?.length || 0);

  const filteredEvents = getFilteredEvents();

  return (
    <div className="maintenance-dashboard-enhanced">
      {/* ===== ENHANCED POPUP WITH ACTIONS ===== */}
      {showPopup && popupTickets.length > 0 && (
        <div className="popup-overlay-enhanced">
          <div className="popup-modal-enhanced">
            <div className="popup-header-enhanced">
              <div className="popup-header-left">
                <span className="popup-header-icon">📋</span>
                <h3>{popupTitle}</h3>
                <span className="popup-count-badge-enhanced">{popupTickets.length}</span>
              </div>
              <button className="popup-close-enhanced" onClick={handlePopupClose}>×</button>
            </div>

            <div className="popup-body-enhanced">
              <p className="popup-subtitle">Showing {popupTickets.length} ticket{popupTickets.length > 1 ? 's' : ''} in this category</p>

              <div className="popup-list-enhanced">
                {popupTickets.map((ticket, index) => {
                  const dueDate = ticket.event_date || ticket.tentative_completion_date;
                  const eventDay = parseLocalDate(dueDate);
                  const today0 = new Date(); today0.setHours(0, 0, 0, 0);
                  const isOverdue = eventDay && eventDay < today0;
                  const daysOverdue = isOverdue ? Math.floor((today0 - eventDay) / (1000 * 60 * 60 * 24)) : 0;
                  const isCritical = ticket.priority === 'Critical' || ticket.priority === 'critical';
                  const isHigh = ticket.priority === 'High' || ticket.priority === 'high';
                  const isPending = ticket.approval_status === 'Pending' || ticket.status === 'pending_approval';
                  const isRejected = isCompletionRejected(ticket);

                  return (
                    <div
                      key={index}
                      className={`popup-item-enhanced ${isCritical ? 'critical-item' : ''} ${isHigh ? 'high-item' : ''} ${isRejected ? 'rejected-item' : ''}`}
                    >
                      <div className="popup-item-main" onClick={() => handlePopupTicketClick(ticket.ticket_id || ticket.id)}>
                        <div className="popup-item-row">
                          <span className="popup-status-badge-enhanced" style={{
                            background: isRejected ? '#dc3545' : isOverdue ? '#dc2626' : getTicketStatusColor(ticket.status)
                          }}>
                            {isRejected ? 'R' : isOverdue ? `${daysOverdue}d` : ticket.status?.substring(0, 3) || 'N/A'}
                          </span>
                          <span className="popup-ticket-number-enhanced">{ticket.ticket_number || `#${ticket.ticket_id || ticket.id}`}</span>
                          {isRejected && <span className="popup-rejected-tag">❌ Rejected</span>}
                          {isCritical && <span className="popup-critical-tag">🚨</span>}
                          {isHigh && !isCritical && <span className="popup-high-tag">⚠️</span>}
                          {isPending && !isRejected && <span className="popup-pending-tag">⏳</span>}
                          {ticket.priority && (
                            <span className={`popup-priority-badge-enhanced ${ticket.priority.toLowerCase()}`}>
                              {ticket.priority}
                            </span>
                          )}
                        </div>
                        <div className="popup-item-row">
                          <span className="popup-description-enhanced">{ticket.title || ticket.issue_description}</span>
                          {isRejected && ticket.manager_comments && (
                            <span className="popup-rejection-feedback">
                              <i className="fas fa-comment"></i> {ticket.manager_comments}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="popup-actions-enhanced">
                        {isManager && isPending && (
                          <>
                            <button
                              className="popup-action-btn approve-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openApprovalModal(ticket, 'approve');
                              }}
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              className="popup-action-btn reject-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openApprovalModal(ticket, 'reject');
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                        {isRejected && (
                          <button
                            className="popup-action-btn reopen-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePopupTicketClick(ticket.ticket_id || ticket.id);
                            }}
                            title="Reopen Ticket"
                          >
                            <i className="fas fa-undo"></i>
                          </button>
                        )}
                        <button
                          className="popup-action-btn view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePopupTicketClick(ticket.ticket_id || ticket.id);
                          }}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="popup-footer-enhanced">
              <button className="popup-btn-primary" onClick={() => setArea('REQUESTS')}>
                <i className="fas fa-list"></i> View All
              </button>
              <button className="popup-btn-secondary" onClick={handlePopupClose}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Access Floating Menu */}
      <div className={`quick-access-menu ${activeQuickMenu ? 'visible' : ''}`}>
        <div className="quick-access-container">
          <button className="quick-access-item" onClick={() => setArea('DASHBOARD')} title="Dashboard">
            <i className="fas fa-th-large"></i><span>Dashboard</span>
          </button>
          <button className="quick-access-item" onClick={() => setArea('REQUESTS')} title="Tickets">
            <i className="fas fa-ticket-alt"></i><span>Tickets</span>
            {stats.open > 0 && <span className="quick-access-badge">{stats.open}</span>}
          </button>
          <button className="quick-access-item" onClick={() => setArea('NEW_REQUEST')} title="New Request">
            <i className="fas fa-plus-circle"></i><span>New</span>
          </button>
          <button className="quick-access-item" onClick={() => setArea('REPORTS')} title="Reports">
            <i className="fas fa-chart-bar"></i><span>Reports</span>
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="dashboard-header-enhanced">
        <div className="header-left-enhanced">
          <div className="header-title-group">
            <h1>Maintenance Dashboard</h1>
            <div className="header-status-indicator">
              <span className="status-dot"></span> Live Updates
            </div>
          </div>
          <p className="header-subtitle-enhanced">Real-time overview of all maintenance activities</p>
        </div>
        <div className="header-right-enhanced">
          <div className="header-actions-enhanced">
            <button
              className={`btn-filter-enhanced ${showOnlyApproved ? 'active' : ''}`}
              onClick={() => {
                setShowOnlyApproved(!showOnlyApproved);
                setTimeout(() => {
                  if (calendarRef && calendarRef.current) {
                    calendarRef.current.getApi().refetchEvents();
                  }
                }, 100);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                background: showOnlyApproved ? '#22c55e' : '#64748b',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              <i className={`fas ${showOnlyApproved ? 'fa-check-circle' : 'fa-eye'}`}></i>
              {showOnlyApproved ? 'Approved Only' : 'All Tickets'}
            </button>
            <button className={`nav-btn-enhanced ${Area === 'DASHBOARD' ? 'active' : ''}`} onClick={() => setArea('DASHBOARD')}>
              <i className="fas fa-th-large"></i><span>Dashboard</span>
            </button>
            <button className={`nav-btn-enhanced ${Area === 'REQUESTS' ? 'active' : ''}`} onClick={() => setArea('REQUESTS')}>
              <i className="fas fa-ticket-alt"></i><span>All Requests</span>
            </button>
            <button className={`nav-btn-enhanced ${Area === 'NEW_REQUEST' ? 'active' : ''}`} onClick={() => setArea('NEW_REQUEST')}>
              <i className="fas fa-plus-circle"></i><span>New Request</span>
            </button>
            <button className={`nav-btn-enhanced ${Area === 'REPORTS' ? 'active' : ''}`} onClick={() => setArea('REPORTS')}>
              <i className="fas fa-chart-bar"></i><span>All Reports</span>
            </button>
            <button className={`nav-btn-enhanced ${Area === 'NEW_REPORT' ? 'active' : ''}`} onClick={() => setArea('NEW_REPORT')}>
              <i className="fas fa-file-alt"></i><span>New Report</span>
            </button>

            <button className="btn-refresh-enhanced" onClick={() => fetchAllData()}>
              <i className="fas fa-sync-alt"></i><span>Refresh</span>
            </button>
            <div className="date-display">
              <i className="far fa-calendar-alt"></i>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
          <div className="user-info-enhanced">
            <div className="user-avatar">{userName ? userName.charAt(0).toUpperCase() : 'U'}</div>
            <div className="user-details">
              <span className="user-name-enhanced">{userName}</span>
              <span className="user-role-enhanced">Maintenance Team</span>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== CONTENT AREA ===================== */}

      {Area === 'DASHBOARD' && (
        <>
          {/* ===== STATS GRID ===== */}
          <div className="stats-grid-enhanced">
            {renderStatCard('📋', 'Total Tickets', stats.total, 'total', '', null, 'total')}
            {renderStatCard('🔓', 'Open Tickets', stats.open, 'open', 'Needs Attention', null, 'open')}
            {renderStatCard('⚡', 'In Progress', stats.inProgress, 'in-progress', 'Active Work', null, 'inProgress')}
            {renderStatCard('✅', 'Completed', stats.completed, 'completed', '', null, 'completed')}
            {renderStatCard('❌', 'Rejected', stats.rejected || 0, 'rejected', 'Needs Reopen', null, 'rejected')}
            {renderStatCard('⏰', 'Overdue', stats.overdue, 'overdue', 'Action Required', null, 'overdue')}
            {renderStatCard('🔴', 'High Priority', stats.highPriority, 'high-priority', 'Urgent', null, 'highPriority')}
            {renderStatCard('🚨', 'Critical', stats.critical, 'critical', 'Immediate Action', null, 'critical')}
            {renderStatCard('⏳', 'Pending Approval', stats.pendingApproval, 'pending-approval', 'Awaiting Review', null, 'pendingApproval')}
          </div>

          {/* ===== 3 COLLAPSIBLE APPROVAL TABLES ===== */}
          {isManager && (
            <div className="approval-tables-section">
              <div className="approval-tables-header">
                <div className="approval-section-title">
                  <h2><i className="fas fa-tasks"></i> Approval Queues</h2>
                  <p>Review and manage pending requests</p>
                </div>
                <div className="approval-section-stats">
                  <span className="stat-chip">
                    <i className="fas fa-clock"></i>
                    {totalPending} Pending
                  </span>
                  {stats.rejected > 0 && (
                    <span className="stat-chip rejected-chip" style={{ background: '#fee2e2', color: '#991b1b' }}>
                      <i className="fas fa-times-circle"></i>
                      {stats.rejected} Rejected
                    </span>
                  )}
                </div>
              </div>

              <div className="approval-tables-grid">
                {renderApprovalTable(
                  'New Requests',
                  pendingNewRequests,
                  'new',
                  'No new requests awaiting approval',
                  showNewRequests,
                  () => setShowNewRequests(!showNewRequests)
                )}

                {renderApprovalTable(
                  'Modification Requests',
                  pendingModificationRequests,
                  'modification',
                  'No modification requests pending',
                  showModificationRequests,
                  () => setShowModificationRequests(!showModificationRequests)
                )}

                {renderApprovalTable(
                  'Completion Approvals',
                  pendingCompletionRequests,
                  'completion',
                  'No completion approvals pending',
                  showCompletionRequests,
                  () => setShowCompletionRequests(!showCompletionRequests)
                )}
              </div>
            </div>
          )}

          {/* ===== DASHBOARD GRID ===== */}
          <div className="dashboard-grid-enhanced">
            <div className="dashboard-card-enhanced calendar-card-enhanced">
              <div className="card-header-enhanced">
                <div className="header-title-section">
                  <i className="fas fa-calendar-week"></i><h3>Maintenance Calendar</h3>
                  <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '10px' }}>
                    (Showing {filteredEvents.length} instances)
                  </span>
                </div>
                <div className="calendar-legend">
                  <div className="legend-item"><span className="legend-dot" style={{ background: '#f59e0b' }}></span>Open</div>
                  <div className="legend-item"><span className="legend-dot" style={{ background: '#3b82f6' }}></span>In Progress</div>
                  <div className="legend-item"><span className="legend-dot" style={{ background: '#22c55e' }}></span>Completed</div>
                  <div className="legend-item"><span className="legend-dot" style={{ background: '#64748b' }}></span>Closed</div>
                  <div className="legend-item"><span className="legend-dot" style={{ background: '#ef4444' }}></span>Cancelled</div>
                  <div className="legend-item"><span className="legend-dot" style={{ background: '#8b5cf6' }}></span>Pending/Submitted</div>
                  <div className="legend-item"><span className="legend-dot" style={{ background: '#dc3545' }}></span>Rejected</div>
                  <div className="legend-item"><span className="legend-dot" style={{ background: '#dc2626' }}></span>Overdue</div>
                </div>
              </div>
              <div className="card-body-enhanced calendar-container-enhanced">
                {filteredEvents.length > 0 ? (
                  <FullCalendar
                    ref={setCalendarRef}
                    key={`calendar-${showOnlyApproved ? 'approved' : 'all'}-${filteredEvents.length}`}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,timeGridWeek,timeGridDay'
                    }}
                    initialView="dayGridMonth"
                    editable={false}
                    selectable={true}
                    selectMirror={true}
                    dayMaxEvents={true}
                    weekends={true}
                    events={filteredEvents}
                    eventClick={handleEventClick}
                    select={handleDateSelect}
                    height="auto"
                    eventTimeFormat={{ hour: '2-digit', minute: '2-digit', meridiem: false }}
                    eventDidMount={(info) => {
                      info.el.style.borderRadius = '6px';
                      info.el.style.padding = '4px 8px';
                      info.el.style.fontSize = '12px';
                      info.el.style.fontWeight = '500';
                      info.el.style.border = 'none';
                      info.el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      const status = info.event.extendedProps.status || 'Unknown';
                      const eventType = info.event.extendedProps.event_type || 'General';
                      const isRecurring = info.event.extendedProps.is_recurring || false;
                      info.el.title = `Type: ${eventType} | Status: ${status}${isRecurring ? ' | Recurring' : ''}`;
                    }}
                  />
                ) : (
                  <div className="no-data-enhanced">
                    <i className="fas fa-calendar-times"></i>
                    <p>
                      {calendarEvents && calendarEvents.length === 0
                        ? 'No scheduled maintenance tasks'
                        : showOnlyApproved
                          ? 'No approved instances found'
                          : 'No instances found'}
                    </p>
                    {calendarEvents && calendarEvents.length > 0 && showOnlyApproved && (
                      <button
                        style={{
                          marginTop: '10px',
                          padding: '8px 20px',
                          background: '#22c55e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setShowOnlyApproved(false);
                          setTimeout(() => {
                            if (calendarRef && calendarRef.current) {
                              calendarRef.current.getApi().refetchEvents();
                            }
                          }, 100);
                        }}
                      >
                        Show All Tickets
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="dashboard-sidebar-enhanced">
              {/* Recent Tickets */}
              <div className="dashboard-card-enhanced recent-tickets-card-enhanced">
                <div className="card-header-enhanced">
                  <div className="header-title-section">
                    <i className="fas fa-history"></i><h3>Recent Tickets</h3>
                  </div>
                  <button className="view-all-link-enhanced" onClick={() => setArea('REQUESTS')}>
                    View All <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
                <div className="card-body-enhanced">
                  {recentTickets && recentTickets.length > 0 ? (
                    <div className="ticket-list-enhanced">
                      {recentTickets.slice(0, 5).map((ticket) => {
                        let typeLabel = 'General';
                        let typeIcon = '📄';
                        const isRejected = isCompletionRejected(ticket);
                        const statusLabel = isRejected ? 'Completion Rejected' : (ticket.status ? ticket.status.replace(/_/g, ' ') : 'Unknown');
                        const statusColor = isRejected ? '#dc3545' : getTicketStatusColor(ticket.status);

                        if (ticket.type === 'Emergency' || ticket.priority === 'Critical' || ticket.priority === 'High') {
                          typeLabel = '🚨 Emergency';
                          typeIcon = '🚨';
                        } else if (ticket.is_recurring) {
                          typeLabel = '📅 Pre-Scheduled';
                          typeIcon = '📅';
                        } else if (ticket.type === 'Facility') {
                          typeLabel = '🏗️ Facility';
                          typeIcon = '🏗️';
                        }

                        return (
                          <div
                            key={ticket.id}
                            className={`ticket-item-enhanced ${isRejected ? 'rejected-ticket' : ''}`}
                            onClick={() => handleTicketClick(ticket.id)}
                            style={{ borderLeftColor: statusColor }}
                          >
                            <div className="ticket-status-indicator" style={{ background: statusColor }}>
                              {isRejected ? '❌' : getTicketStatusIcon(ticket.status)}
                            </div>
                            <div className="ticket-info-enhanced">
                              <div className="ticket-header-row">
                                <div className="ticket-number-enhanced">{ticket.ticket_number || `#${ticket.id}`}</div>
                                <span className={`status-badge-enhanced ${isRejected ? 'rejected' : ticket.status}`} style={{ background: statusColor }}>
                                  {statusLabel}
                                </span>
                              </div>
                              <div className="ticket-title-enhanced">{ticket.issue_description || 'No description'}</div>
                              {isRejected && ticket.manager_comments && (
                                <div className="ticket-rejection-feedback" style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>
                                  <i className="fas fa-comment"></i> {ticket.manager_comments}
                                </div>
                              )}
                              <div className="ticket-footer-row">
                                <span className={`priority-indicator ${ticket.priority ? ticket.priority.toLowerCase() : 'low'}`}>
                                  <i className="fas fa-flag"></i>{ticket.priority || 'Low'}
                                </span>
                                <span className="ticket-type-label">
                                  {typeIcon} {typeLabel}
                                </span>
                                {ticket.type === 'prescheduled' || ticket.is_recurring ? (
                                  <span className="ticket-date-label">
                                    <i className="far fa-calendar"></i>
                                    Effective: {ticket.schedule_start_date || 'Not set'}
                                  </span>
                                ) : (
                                  <span className="ticket-date-label">
                                    <i className="far fa-calendar"></i>
                                    Tentative: {ticket.tentative_completion_date || 'Not set'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-data-enhanced">
                      <i className="fas fa-inbox"></i><p>No recent tickets found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Team Status */}
              <div className="dashboard-card-enhanced team-status-card-enhanced">
                <div className="card-header-enhanced">
                  <div className="header-title-section">
                    <i className="fas fa-users"></i><h3>Team Status</h3>
                  </div>
                </div>
                <div className="card-body-enhanced">
                  {teamStatus && teamStatus.length > 0 ? (
                    <div className="team-list-enhanced">
                      {teamStatus.map((team) => (
                        <div key={team.name} className="team-item-enhanced">
                          <div className="team-avatar">{team.name.charAt(0)}</div>
                          <div className="team-info-enhanced">
                            <div className="team-name-enhanced">{team.name}</div>
                            <div className="team-lead-enhanced">Tasks: {team.total_tasks}</div>
                          </div>
                          <div className="team-stats-enhanced">
                            <span className="team-active-tasks">{team.active_tasks} active</span>
                            <span className="team-members-count">{team.members || 1} member</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-data-enhanced">
                      <i className="fas fa-user-friends"></i><p>No team assignments yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="dashboard-card-enhanced quick-actions-card-enhanced">
                <div className="card-header-enhanced">
                  <div className="header-title-section">
                    <i className="fas fa-bolt"></i><h3>Quick Actions</h3>
                  </div>
                </div>
                <div className="card-body-enhanced">
                  <div className="quick-actions-grid-enhanced">
                    <button className="quick-action-btn-enhanced" onClick={() => setArea('NEW_REQUEST')}>
                      <div className="action-icon-wrapper"><i className="fas fa-plus-circle"></i></div>
                      <span>New Request</span>
                    </button>
                    <button className="quick-action-btn-enhanced" onClick={() => setArea('NEW_REPORT')}>
                      <div className="action-icon-wrapper"><i className="fas fa-file-alt"></i></div>
                      <span>New Report</span>
                    </button>
                    <button className="quick-action-btn-enhanced" onClick={() => setArea('REQUESTS')}>
                      <div className="action-icon-wrapper"><i className="fas fa-list-check"></i></div>
                      <span>All Tickets</span>
                    </button>
                    <button className="quick-action-btn-enhanced" onClick={() => setArea('SERVICES')}>
                      <div className="action-icon-wrapper"><i className="fas fa-cogs"></i></div>
                      <span>Services</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {Area === 'REQUESTS' && <AllRequests onTicketClick={handleTicketClick} />}
      {Area === 'NEW_REQUEST' && <MaintenanceForm />}
      {Area === 'REPORTS' && <ShiftReportAll />}
      {Area === 'NEW_REPORT' && <NewShiftReport />}
      {Area === 'SERVICES' && <ServiceTypeTable />}
      {Area === 'TICKET_DETAIL' && (
        <TicketDetail ticketId={activeTicketId} onBack={() => setArea('DASHBOARD')} />
      )}

      {/* Ticket Detail Modal */}
      {showTicketModal && selectedTicket && (
        <div className="ticket-modal-overlay-enhanced">
          <div className="ticket-modal-enhanced">
            <div className="modal-header-enhanced">
              <h3>Ticket {selectedTicket.ticket_number || `#${selectedTicket.id}`}</h3>
              <button className="modal-close-enhanced" onClick={() => setShowTicketModal(false)}>×</button>
            </div>
            <div className="modal-body-enhanced">
              <p><strong>Description:</strong> {selectedTicket.issue_description || 'No description'}</p>
              <p><strong>Status:</strong> {selectedTicket.status}</p>
              <p><strong>Priority:</strong> {selectedTicket.priority}</p>
              {selectedTicket.assigned_to && <p><strong>Assigned To:</strong> {selectedTicket.assigned_to}</p>}
            </div>
            <div className="modal-footer-enhanced">
              <button className="btn-primary-enhanced" onClick={() => handleTicketClick(selectedTicket.id)}>
                View Full Details
              </button>
              <button className="btn-secondary-enhanced" onClick={() => setShowTicketModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== APPROVAL MODAL ===== */}
      {showApprovalModal && (
        <div className="approval-modal-overlay" onClick={() => setShowApprovalModal(false)}>
          <div className="approval-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="approval-modal-header">
              <h5>
                <i className={`fas ${approvalAction === 'approve' ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'}`}></i>
                {approvalAction === 'approve' ? 'Approve' : 'Reject'} Request
              </h5>
              <button className="approval-modal-close" onClick={() => setShowApprovalModal(false)}>×</button>
            </div>
            <div className="approval-modal-body">
              <div className="approval-ticket-summary">
                <div className="summary-row">
                  <span className="summary-label">Ticket:</span>
                  <span className="summary-value">{selectedApprovalTicket?.ticket_number}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Issue:</span>
                  <span className="summary-value">{selectedApprovalTicket?.issue_description}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Requester:</span>
                  <span className="summary-value">{selectedApprovalTicket?.requested_by}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Type:</span>
                  <span className="summary-value">
                    {selectedApprovalTicket?.type === 'prescheduled' ? 'Pre-scheduled' : 'Emergent'}
                    {selectedApprovalTicket?.is_recurring === 1 && ' (Recurring)'}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Priority:</span>
                  <span className="summary-value">{selectedApprovalTicket?.priority || 'Low'}</span>
                </div>

                {selectedApprovalTicket?.approval_type === 'modification' && selectedApprovalTicket?.pending_data && (
                  <div className="summary-row modification-changes">
                    <span className="summary-label">Proposed Changes:</span>
                    <span className="summary-value">
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => {
                          try {
                            const changes = JSON.parse(selectedApprovalTicket.pending_data);
                            alert('Proposed Changes:\n' + JSON.stringify(changes, null, 2));
                          } catch (e) {
                            alert(selectedApprovalTicket.pending_data);
                          }
                        }}
                      >
                        <i className="fas fa-eye"></i> View Changes
                      </button>
                    </span>
                  </div>
                )}

                {selectedApprovalTicket?.approval_type === 'completion' && selectedApprovalTicket?.completion_notes && (
                  <div className="summary-row">
                    <span className="summary-label">Completion Notes:</span>
                    <span className="summary-value">{selectedApprovalTicket?.completion_notes}</span>
                  </div>
                )}

                {(!selectedApprovalTicket?.approval_type || selectedApprovalTicket?.approval_type === '' || selectedApprovalTicket?.approval_type === null) && (
                  <>
                    {selectedApprovalTicket?.schedule_start_date && (
                      <div className="summary-row">
                        <span className="summary-label">Schedule Start:</span>
                        <span className="summary-value">{selectedApprovalTicket?.schedule_start_date}</span>
                      </div>
                    )}
                    {selectedApprovalTicket?.recurrence_type && selectedApprovalTicket?.recurrence_type !== 'none' && (
                      <div className="summary-row">
                        <span className="summary-label">Recurrence:</span>
                        <span className="summary-value">
                          {selectedApprovalTicket?.recurrence_type?.charAt(0).toUpperCase() + selectedApprovalTicket?.recurrence_type?.slice(1)}
                          {selectedApprovalTicket?.recurrence_day && ` (${selectedApprovalTicket?.recurrence_day})`}
                          {selectedApprovalTicket?.recurrence_date && ` on day ${selectedApprovalTicket?.recurrence_date}`}
                        </span>
                      </div>
                    )}
                    {selectedApprovalTicket?.tentative_completion_date && (
                      <div className="summary-row">
                        <span className="summary-label">Tentative Completion:</span>
                        <span className="summary-value">{selectedApprovalTicket?.tentative_completion_date}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* NEW: Editable fields for new-request approvals only.
                  Manager can correct details before approving. On submit, if anything
                  changed, we PATCH /maintenance/api/tickets/:id (the same edit endpoint
                  used elsewhere) before calling the approve endpoint. */}
              {approvalAction === 'approve' && (!selectedApprovalTicket?.approval_type || selectedApprovalTicket?.approval_type === 'new') && (
                <div className="approval-edit-section" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e5e7eb' }}>
                  <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    <i className="fas fa-edit"></i> Edit Details Before Approving
                  </label>

                  <div className="approval-form-group">
                    <label>Issue Description</label>
                    <textarea
                      className="approval-form-control"
                      rows="2"
                      value={editedTicketData.issue_description || ''}
                      onChange={(e) => setEditedTicketData(prev => ({ ...prev, issue_description: e.target.value }))}
                    />
                  </div>

                  <div className="approval-form-group">
                    <label>Category</label>
                    <input
                      type="text"
                      className="approval-form-control"
                      value={editedTicketData.maintenance_category || ''}
                      onChange={(e) => setEditedTicketData(prev => ({ ...prev, maintenance_category: e.target.value }))}
                    />
                  </div>

                  <div className="approval-form-group">
                    <label>Priority</label>
                    <select
                      className="approval-form-control"
                      value={editedTicketData.priority || 'Low'}
                      onChange={(e) => setEditedTicketData(prev => ({ ...prev, priority: e.target.value }))}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>

                  {(selectedApprovalTicket?.type === 'prescheduled' || selectedApprovalTicket?.is_recurring) ? (
                    <>
                      <div className="approval-form-group">
                        <label>Schedule Start Date</label>
                        <input
                          type="date"
                          className="approval-form-control"
                          value={editedTicketData.schedule_start_date || ''}
                          onChange={(e) => setEditedTicketData(prev => ({ ...prev, schedule_start_date: e.target.value }))}
                        />
                      </div>
                      <div className="approval-form-group">
                        <label>Schedule End Date</label>
                        <input
                          type="date"
                          className="approval-form-control"
                          value={editedTicketData.schedule_end_date || ''}
                          onChange={(e) => setEditedTicketData(prev => ({ ...prev, schedule_end_date: e.target.value }))}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="approval-form-group">
                      <label>Tentative Completion Date</label>
                      <input
                        type="date"
                        className="approval-form-control"
                        value={editedTicketData.tentative_completion_date || ''}
                        onChange={(e) => setEditedTicketData(prev => ({ ...prev, tentative_completion_date: e.target.value }))}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="approval-form-group">
                <label htmlFor="approvalComments">
                  Manager Comments <span className="text-muted">(Optional)</span>
                </label>
                <textarea
                  id="approvalComments"
                  className="approval-form-control"
                  rows="3"
                  value={approvalComments}
                  onChange={(e) => {
                    setApprovalComments(e.target.value);
                    if (approvalError) setApprovalError('');
                  }}
                  placeholder="Add any additional comments here... (Optional)"
                />
              </div>

              {approvalError && (
                <div className="alert alert-danger" style={{ marginTop: '10px' }}>
                  <i className="fas fa-exclamation-circle"></i> {approvalError}
                </div>
              )}
            </div>
            <div className="approval-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowApprovalModal(false)}>Cancel</button>
              <button
                className={`btn ${approvalAction === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleApprovalSubmit}
                disabled={isSubmittingApproval}
              >
                {isSubmittingApproval ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  approvalAction === 'approve' ? 'Approve' : 'Reject'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceDashboard;